/**
 * What a child has: acorns, and how many days in a row they have come back.
 *
 * Read from the server rather than counted here. Balances are a `SUM` over the
 * append-only ledgers ([D-045](../../../docs/DECISIONS.md#d-045)), so this is
 * always the same number the rows add up to — there is no client-side total to
 * drift from it.
 *
 * XP is deliberately **not** here. It is a record of learning rather than
 * something to spend, and putting a second big number next to the spendable one
 * invites a child to read them as the same thing. It belongs on the recap and
 * the profile.
 */

import { useEffect, useState } from 'react';
import { type Client, type Wallet as Held, onChanged } from './api.js';

export default function Wallet({ client }: { readonly client: Client }) {
  const [held, setHeld] = useState<Held | null>(null);

  useEffect(() => {
    let live = true;
    // Several changes in a row (a chest that gives acorns *and* a pet) are
    // read once, after the last of them.
    let pending: ReturnType<typeof setTimeout> | null = null;
    const read = () => {
      client.learner.wallet
        .query()
        .then((next) => {
          if (live) setHeld(next);
        })
        .catch(() => {
          // A wallet that will not load is not worth an error screen: the
          // child came here to play, and the number reappears on the next read.
        });
    };
    read();
    // Whatever spent or earned acorns — the shop, a chest, a dye, a title —
    // the number up here follows it, rather than waiting for the next lesson.
    const stop = onChanged(() => {
      if (pending !== null) clearTimeout(pending);
      pending = setTimeout(() => {
        pending = null;
        read();
      }, 50);
    });
    return () => {
      live = false;
      stop();
      if (pending !== null) clearTimeout(pending);
    };
  }, [client]);

  return (
    <div className="row" style={{ gap: 8 }}>
      <span className="pill" aria-label={`${String(held?.acorns ?? 0)} acorns`}>
        <span aria-hidden="true">🌰</span> {held?.acorns ?? 0}
      </span>
      {(held?.streak ?? 0) > 0 && (
        <span
          className="pill"
          style={{ color: 'var(--flame-ink)' }}
          aria-label={`${String(held?.streak ?? 0)} days in a row`}
        >
          <span aria-hidden="true">🔥</span> {held?.streak ?? 0}
        </span>
      )}
    </div>
  );
}
